import React, { useMemo } from 'react';
import { BUTTON_TYPES, components as Core, useTranslation } from '../../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../../styles';
import { AuthOnboardingLogs } from '../AuthOnboardingLogs';
import { CompanyDetailsFooter } from '../CompanyDetailsFooter';
import { CollapsedFieldSection } from '../CompanyDetailsFields';
import { DATA_REMOVE_FIELDS, FIELD_TYPES } from '../../utils/constants';
import CustomizeUIModal from '../../../CustomizeUIModal';

/**
 * Renders company customization, data-retention, onboarding-log, and deletion controls.
 *
 * @param {object} props
 * @param {object} props.company
 * @param {string} props.companyId
 * @param {Function} props.getFieldValue
 * @param {boolean} props.hasChanges
 * @param {boolean} props.isDeleting
 * @param {boolean} props.isUpdatingCompany
 * @param {Function} props.onChangeProperty
 * @param {Function} props.onClose
 * @param {Function} props.onDeleteCompany
 * @param {Function} props.onSave
 */
export const AdministrationTab = ({
    company,
    companyId,
    getFieldValue,
    hasChanges,
    isDeleting = false,
    isUpdatingCompany,
    onChangeProperty,
    onClose,
    onDeleteCompany,
    onSave
}) => {
    const { t } = useTranslation();
    const dataRemovalFields = useMemo(() => [
        {
            fieldName: DATA_REMOVE_FIELDS.dataLifetime,
            label: t('companies.dataLifetime'),
            type: FIELD_TYPES.number,
            onChange: onChangeProperty(DATA_REMOVE_FIELDS.dataLifetime)
        },
        {
            fieldName: DATA_REMOVE_FIELDS.ordersDataLifetime,
            label: t('companies.ordersDataLifetime'),
            type: FIELD_TYPES.number,
            onChange: onChangeProperty(DATA_REMOVE_FIELDS.ordersDataLifetime)
        },
        {
            fieldName: DATA_REMOVE_FIELDS.feedbacksDataLifetime,
            label: t('companies.feedbacksDataLifetime'),
            type: FIELD_TYPES.number,
            onChange: onChangeProperty(DATA_REMOVE_FIELDS.feedbacksDataLifetime)
        },
        {
            fieldName: DATA_REMOVE_FIELDS.archiveThreadsLifetime,
            label: t('companies.archiveThreadsLifetime'),
            type: FIELD_TYPES.number,
            onChange: onChangeProperty(DATA_REMOVE_FIELDS.archiveThreadsLifetime)
        },
        {
            fieldName: DATA_REMOVE_FIELDS.deletedDataLifetime,
            label: t('companies.deletedDataLifetime'),
            type: FIELD_TYPES.number,
            onChange: onChangeProperty(DATA_REMOVE_FIELDS.deletedDataLifetime)
        }
    ], [onChangeProperty, t]);

    return (
        <div>
            <Core.Collapse
                data={[{
                    title: t('companies.customizeUI.customizeUI'),
                    content: <CustomizeUIModal companyId={companyId} isEmbedded={true} />,
                    key: 'customization'
                }]}
            />
            <div className={css(styles.settingsSection)}>
                <CollapsedFieldSection
                    title={t('companies.dataRemoveCollapse')}
                    fields={dataRemovalFields}
                    itemKey="dataRemovalDetails"
                    getFieldValue={getFieldValue}
                />
                <AuthOnboardingLogs company={company} />
                <section className={css(styles.dangerZone)}>
                    <h3 className={css(styles.dangerTitle)}>{t('companies.details.dangerZone.title')}</h3>
                    <p className={css(styles.dangerDescription)}>{t('companies.deleteCompanyConfirm')}</p>
                    <Core.PopConfirm onConfirm={onDeleteCompany}>
                        <span>
                            <Core.Button
                                label={t('companies.deleteCompany')}
                                isLoading={isDeleting}
                                type={BUTTON_TYPES.redOutline}
                            />
                        </span>
                    </Core.PopConfirm>
                </section>
            </div>
            <CompanyDetailsFooter
                hasChanges={hasChanges}
                isUpdatingCompany={isUpdatingCompany}
                onClose={onClose}
                onSave={onSave}
            />
        </div>
    );
};

const styles = StyleSheet.create({
    settingsSection: {
        marginTop: spacing.s5,
        paddingTop: spacing.s4,
        borderTop: `1px solid ${colors.greyLight}`
    },
    dangerZone: {
        marginTop: spacing.s5,
        padding: spacing.s3,
        border: `1px solid ${colors.redOpacity}`,
        borderRadius: spacing.s1,
        backgroundColor: colors.redOpacity
    },
    dangerTitle: {
        ...fonts.mdBold,
        color: colors.red,
        marginBottom: spacing.s1
    },
    dangerDescription: {
        ...fonts.smNormal,
        color: colors.blackOpacity,
        marginBottom: spacing.s3
    }
});
